import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { getReleaseMessaging } from 'utils/releaseMessaging';
import { getCurrentRegionSettings, getStudioConfig } from 'utils/studioConfig';
import tryToPlayVideo from 'utils/tryToPlayVideo';
import { joinClasses } from 'utils/utils';

import ArrowDown from 'components/bolt/icons/arrowDown';
import SocialsEditor from 'components/editor/movies/home/shared/socialsEditor';
import MovieEditor from 'components/editor/movies/movieEditor';
import SelectableSection from 'components/editor/selectableSection';
import MediaField from 'components/editor/settings/mediaField';
import SimpleField from 'components/editor/settings/simpleField';
import ToggleField from 'components/editor/settings/toggleField';
import IconPlus from 'components/icons/plus';
import InlineSelect from 'components/inlineSelect/inlineSelect';
import GoToMediaButton from 'components/media/goToMediaButton';

import s from 'components/editor/movies/home/home.sss';

export default class HomeEditor extends MovieEditor {
	s = s;

	constructor(props) {
		super(props);

		this.setHeroMode = this.setHeroMode.bind(this);
		this.setHeroBackgroundType = this.setHeroBackgroundType.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();

		const bgVid = this.base.querySelector(`.${this.s.background?.split(' ')[0]} video`);
		if (bgVid && bgVid.autoplay && bgVid.paused && bgVid.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
			bgVid.play();
		}
	}

	getBackground() {
		// Use desktop assets for CMS visualization
		const bg = this.movie?.pages?.home?.bg;
		const hero = this.movie?.hero;
		const type = bg?.type || hero?.type;

		if (type === 'video') {
			const video = bg?.desktop?.video?.custom ? bg.desktop.video.url : hero?.desktop?.video?.url;
			const image = bg?.desktop?.fallbackImg?.custom ? bg.desktop.fallbackImg.url : hero?.desktop?.fallbackImg?.url;

			return { video, image };
		}

		const image = bg?.desktop?.still?.custom ? bg.desktop.still.url : hero?.desktop?.still?.url;
		return { video: null, image };
	}

	getTT() {
		const tt = this.movie?.pages?.home?.tt;
		return tt?.url || tt;
	}

	playVideo(e) {
		tryToPlayVideo(e.target);
	}

	renderBackground() {
		const background = this.getBackground();
		const backgroundStyle = {};
		if (background?.image) {
			backgroundStyle.backgroundImage = `url("${encodeURI(background.image)}")`;
		}

		return (
			<SelectableSection {...this.props} class={this.s.background} key="home-bg" style={backgroundStyle} section="/pages/home/bg">
				{!!background?.video && (
					<video
						playsInline muted autoplay loop
						onCanPlay={this.playVideo}
						src={background.video}
					/>
				)}
			</SelectableSection>
		);
	}

	renderTT() {
		const ttSrc = this.getTT();

		if (!ttSrc) {
			return (
				<button class={joinClasses(this.s.tt, this.s.add)} key="tt-add">
					<IconPlus /><span>Add logo</span>
				</button>
			);
		}

		return (
			<img class={this.s.tt} src={ttSrc} alt="" key="tt" />
		);
	}

	renderRelease() {
		const releaseMessaging = this.movie?.pages?.home?.releaseMessaging;
		if (!(releaseMessaging?.enabled ?? true)) return;
		const text = releaseMessaging?.text ?? getReleaseMessaging(this.movie, undefined, this.state.thundrData?.copy?.baseCopy);

		return <div class={this.s.release} key="release">{text}</div>;
	}

	renderHeroContent() {}

	renderHero() {
		return (
			<SelectableSection {...this.props} class={joinClasses(this.s.details, this.s.home)} key="details" section="/pages/home/hero">
				{this.renderTT()}
				{this.renderRelease()}
				{this.renderHeroContent()}
			</SelectableSection>
		);
	}

	renderContent() {
		return (
			<div class={joinClasses(this.s.wrapper, this.props.class)}>
				{this.renderBackground()}
				{this.renderHero()}
			</div>
		);
	}

	renderVisual() {
		const props = this.props;
		const config = getStudioConfig();

		return (
			<SelectableSection class={this.s.container} section="/" {...props}>
				{this.renderContent()}
				{config.showScrollArrows && (
					<div class={this.s.arrowBtn} key="arrow">
						<ArrowDown class={this.s.arrowDown} aria-hidden />
					</div>
				)}
				{config.showSocials && <SocialsEditor {...props} class={this.s.socials} key="socials" />}
			</SelectableSection>
		);
	}

	setHeroMode(e) {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/heroMode`, e.target.value);
	}

	setHeroBackgroundType(e) {
		const value = e.target.value;
		if (value === 'default') {
			jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/bg/type`, null);
		} else {
			jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/bg/type`, e.target.value);
		}
	}

	renderBackgroundField(opts = {}) {
		const { data, dataRoot } = this.props;
		const { label, defaultPath, customPath, video, ...props } = opts;
		const useCustom = jsonPointer.get(data, `${customPath}/custom`);
		const type = video ? 'video' : 'image';

		return (
			<div class={this.s.itemGroup}>
				<h3>{label}</h3>
				<ToggleField dark class={this.s.simpleToggle} path={`${dataRoot}${customPath}/custom`}>
					Use unique {type}
				</ToggleField>
				<MediaField
					dark
					video={video}
					label={`Replace ${type}`}
					path={useCustom ? `${dataRoot}${customPath}/url` : `${dataRoot}${defaultPath}/url`}
					inheritPath={!useCustom && `${dataRoot}/base_movie${defaultPath}/url`}
					group={data.id}
					disabled={!useCustom}
					{...props}
				/>
			</div>
		);
	}

	renderBackgroundSettings() {
		const homeContent = this.movie?.pages?.home;
		const heroContent = this.movie?.hero;

		const selectedType = homeContent?.bg?.type || heroContent?.type || 'image';
		const isVideo = selectedType === 'video';

		return (
			<div class={this.s.itemEditor}>
				<div class={this.s.itemGroup}>
					<h3>Background type</h3>
					<InlineSelect
						class={this.s.backgroundType}
						name="hero-bg-type"
						value={homeContent?.bg?.type}
						defaultValue="default"
						onChange={this.setHeroBackgroundType}
						fullWidth
						dark
					>
						<option value="default">Default</option>
						<option value="video">Video</option>
						<option value="image">Still</option>
					</InlineSelect>
				</div>

				{isVideo && this.renderBackgroundField({
					video: true,
					label: 'Hero video (Landscape)',
					defaultPath: '/content/hero/desktop/video',
					customPath: '/content/pages/home/bg/desktop/video',
					assetData: { type: 'hero-video', aspectRatio: '16:9', recommendedDimensions: '1920:1080' },
					helpTip: 'Used for desktop and tablet'
				})}

				{isVideo && this.renderBackgroundField({
					video: true,
					label: 'Hero video (Portrait)',
					defaultPath: '/content/hero/mobile/video',
					customPath: '/content/pages/home/bg/mobile/video',
					assetData: { type: 'hero-video-mobile', recommendedDimensions: '540:800' },
					helpTip: 'Used for mobile'
				})}

				{isVideo && this.renderBackgroundField({
					video: false,
					label: 'Hero video first frame (Landscape)',
					defaultPath: '/content/hero/desktop/fallbackImg',
					customPath: '/content/pages/home/bg/desktop/fallbackImg',
					assetData: { type: 'hero-video-fallback', aspectRatio: '16:9', recommendedDimensions: '1920:1080' },
					helpTip: 'Used for desktop and tablet as a placeholder while the video is loading'
				})}

				{isVideo && this.renderBackgroundField({
					video: false,
					label: 'Hero video first frame (Portrait)',
					defaultPath: '/content/hero/mobile/fallbackImg',
					customPath: '/content/pages/home/bg/mobile/fallbackImg',
					assetData: { type: 'hero-video-fallback-mobile', recommendedDimensions: '540:800' },
					helpTip: 'Used for mobile as a placeholder while the video is loading'
				})}

				{!isVideo && this.renderBackgroundField({
					video: false,
					label: 'Hero still (Landscape)',
					defaultPath: '/content/hero/desktop/still',
					customPath: '/content/pages/home/bg/desktop/still',
					assetData: { type: 'hero-still', aspectRatio: '16:9', recommendedDimensions: '1920:1080' },
					helpTip: 'Used for desktop and tablet'
				})}

				{!isVideo && this.renderBackgroundField({
					video: false,
					label: 'Hero still (Portrait)',
					defaultPath: '/content/hero/mobile/still',
					customPath: '/content/pages/home/bg/mobile/still',
					assetData: { type: 'hero-still-mobile', recommendedDimensions: '540:800' },
					helpTip: 'Used for mobile'
				})}
			</div>
		);
	}

	renderReleaseMessagingSettings() {
		const path = `${this.props.dataRoot}/content/pages/home/releaseMessaging`;
		const showMessaging = this.movie?.pages?.home?.releaseMessaging?.enabled ?? true;

		return (
			<div class={this.s.itemGroup}>
				<h3>Release messaging</h3>
				<SimpleField
					dark
					toggleable
					path={`${path}/text`}
					label="Use unique release messaging"
					disabled={!showMessaging}
					defaultValue={getReleaseMessaging(this.movie, this.movie.status, this.state.thundrData?.copy?.baseCopy)}
				/>
				{/* eslint-disable-next-line react/jsx-boolean-value -- explicit true makes more sense for defaultValue (it is not a toggleable value) */}
				<ToggleField dark class={this.s.simpleToggle} path={`${path}/enabled`} defaultValue={true}>
					Show release messaging
				</ToggleField>
			</div>
		);
	}

	renderTTSettings() {
		return (
			<div class={this.s.itemGroup}>
				<h3>Title treatment</h3>
				<span>Title treatment can be edited from the movie page media tab.</span>
				<GoToMediaButton dark movie={this.movie} />
			</div>
		);
	}

	renderHeroContentSettings() {}

	renderHeroSettings() {
		const homeEntEnabled = getCurrentRegionSettings()?.homeEnt; // If homeEnt is enabled, allow switching to Preview Panel
		const heroMode = homeEntEnabled && this.movie?.pages?.home?.heroMode || 'standard';
		return (
			<div>
				{/* Segmented Control for Mode Toggle */}
				{homeEntEnabled && (
					<InlineSelect dark fullWidth name="hero-mode" onChange={this.setHeroMode} class={this.s.modeSelect} value={heroMode}>
						<option value="standard">Standard</option>
						<option value="preview">Preview panel</option>
					</InlineSelect>
				)}
	
				{/* Conditional settings based on mode */}
				<div class={this.s.modeContent}>
					<div class={this.s.itemEditor}>
						{/* Hero specific settings are handled inside their own component */}
						{this.renderHeroContentSettings()}
						{this.renderReleaseMessagingSettings()}
						{this.renderTTSettings()}
					</div>
				</div>
			</div>
		);
	}
	
	renderHomeSettings() {
		const options = [
			{ label: 'Background', path: '/pages/home/bg' },
			{ label: 'Hero', path: '/pages/home/hero' },
			{ label: 'Social media', path: '/pages/home/socials' },
		];
	
		return (
			<div class={this.s.itemGroup}>
				{options.map(({ label, path }) => (
					<div class={this.s.item} key={path}>
						<span class={this.s.label}>{label}</span>
						<button class={this.s.edit} onClick={() => this.props.select(path)}>
							View
						</button>
					</div>
				))}
			</div>
		);
	}

	renderSettings() {
		const activeElement = this.props.activeElement;
	
		let content;
		let nav = [{ label: 'Home', path: `/` }];
	
		// Handle background settings navigation
		if (activeElement === '/pages/home/bg') {
			nav.push({ label: 'Background', path: activeElement });
			content = this.renderBackgroundSettings();
		}
	
		// Handle hero settings navigation
		else if (activeElement === '/pages/home/hero') {
			nav.push({ label: 'Hero', path: activeElement });
			content = this.renderHeroSettings();
		}

		// Handle social media settings navigation
		else if (activeElement.startsWith('/pages/home/socials')) {
			return <SocialsEditor {...this.props} />;
		}
	
		// Main home settings
		else {
			// nav is just ['Home'] in this case
			content = this.renderHomeSettings();
		}
	
		return (
			<div class={this.s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}
